import { ThemeOptions } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ReactNode, useMemo } from 'react';

import { customShadowsV2 } from './v2/custom-shadows-v2';
import { componentsOverridesV2 } from './v2/overrides';
import { paletteV2 } from './v2/palette-v2';
import { shadowsV2 } from './v2/shadows-v2';
import { typographyV2 } from './v2/typography-v2';

type ThemeProviderProps = {
  children: ReactNode;
};

// ----------------------------------------------------------------------
// Single theme for both products (Jooice 2 and Spartacus).
//
// Until Aug 2026 this provider could build one of two themes, selected by the
// VITE_NEW_DESIGN_SYSTEM_JOOICE / _SPARTACUS env flags (plus a `forceV2` prop
// that pinned every Spartacus route to v2). That made the rendered theme depend
// on an untracked .env — the test suite got v1 in CI and v2 locally — and left
// Jooice 2 permanently forked from the design system. There is now one theme: v2.
// ----------------------------------------------------------------------

export default function ThemeProvider({ children }: ThemeProviderProps) {
  const memoizedValue = useMemo(
    () => ({
      palette: paletteV2('light'),
      shadows: shadowsV2('light'),
      customShadows: customShadowsV2('light'),
      shape: { borderRadius: 12 },
      typography: typographyV2,
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },
    }),
    [],
  );

  const theme = createTheme(memoizedValue as ThemeOptions);

  theme.components = componentsOverridesV2(theme);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
